/**
 * Student fields that can be mapped onto a WhatsApp template variable.
 *
 * Used by every surface that maps a Meta template placeholder against a student:
 * the broadcast composer and the class-started automation (WhatsAppDashboard),
 * and scheduled messages (ScheduledMessagesTab).
 *
 * Must stay in sync with STUDENT_VARS in vc_be/utils/whatsappTemplateVars.js —
 * the backend rejects any `variable` outside that allow-list, so an entry added
 * here without its counterpart there silently sends as an empty string.
 *
 * Values are resolved server-side, per recipient, at send time. The FE only
 * needs the value/label/group to build the picker.
 */

/** Display order of the optgroups. */
export const STUDENT_VAR_GROUP_ORDER = [
  "Student", "Fees", "Course", "Batch", "Institute", "Date & time",
];

export const STUDENT_TEMPLATE_VARS = [
  /* Student */
  { value: "student_name", label: "Student name", group: "Student" },
  { value: "student_first_name", label: "Student first name", group: "Student" },
  { value: "student_mobile", label: "Student mobile", group: "Student" },
  { value: "student_email", label: "Student email", group: "Student" },
  { value: "signup_link", label: "Account sign-up link", group: "Student" },
  { value: "student_status", label: "Student status", group: "Student" },
  { value: "student_gender", label: "Gender", group: "Student" },
  { value: "student_dob", label: "Date of birth", group: "Student" },
  { value: "student_address", label: "Student address", group: "Student" },
  { value: "student_joined_date", label: "Joining date", group: "Student" },
  { value: "student_degree", label: "Degree", group: "Student" },
  { value: "student_passout_year", label: "Passout year", group: "Student" },
  { value: "student_experience", label: "Experience", group: "Student" },
  { value: "guardian_name", label: "Guardian name", group: "Student" },
  { value: "guardian_mobile", label: "Guardian mobile", group: "Student" },
  { value: "employee_id", label: "Employee ID", group: "Student" },
  { value: "department", label: "Department", group: "Student" },
  { value: "job_role", label: "Job role", group: "Student" },

  /* Fees */
  { value: "pending_fees", label: "Pending fees", group: "Fees" },
  { value: "paid_fees", label: "Paid fees", group: "Fees" },
  { value: "total_fees", label: "Total fees", group: "Fees" },

  /* Course */
  { value: "course_name", label: "Course name", group: "Course" },
  { value: "course_code", label: "Course code", group: "Course" },
  { value: "course_duration", label: "Course duration", group: "Course" },
  { value: "course_mode", label: "Course mode", group: "Course" },
  { value: "course_fees", label: "Course fees", group: "Course" },
  { value: "course_start_date", label: "Course start date", group: "Course" },
  { value: "course_end_date", label: "Course end date", group: "Course" },
  { value: "course_category", label: "Course category", group: "Course" },
  { value: "course_level", label: "Course level", group: "Course" },

  /* Batch */
  { value: "batch_name", label: "Batch name", group: "Batch" },
  { value: "batch_start_date", label: "Batch start date", group: "Batch" },
  { value: "batch_end_date", label: "Batch end date", group: "Batch" },
  { value: "batch_timing", label: "Batch timing", group: "Batch" },
  { value: "batch_start_time", label: "Batch start time", group: "Batch" },
  { value: "batch_mode", label: "Batch mode", group: "Batch" },
  { value: "batch_status", label: "Batch status", group: "Batch" },
  { value: "batch_type", label: "Batch type (weekday/weekend)", group: "Batch" },
  { value: "batch_room", label: "Classroom / room", group: "Batch" },
  { value: "batch_meeting_link", label: "Class meeting link", group: "Batch" },
  { value: "instructor_name", label: "Instructor name", group: "Batch" },

  /* Institute */
  { value: "institute_name", label: "Institute name", group: "Institute" },
  { value: "brand_name", label: "Brand name", group: "Institute" },
  { value: "legal_name", label: "Legal / company name", group: "Institute" },
  { value: "institute_email", label: "Institute email", group: "Institute" },
  { value: "institute_phone", label: "Institute phone", group: "Institute" },
  { value: "institute_address", label: "Institute address", group: "Institute" },
  { value: "portal_url", label: "Login / portal link", group: "Institute" },

  /* Date & time */
  { value: "current_date", label: "Today's date", group: "Date & time" },
  { value: "current_time", label: "Current time", group: "Date & time" },
  { value: "current_day", label: "Current weekday", group: "Date & time" },
  { value: "current_month", label: "Current month", group: "Date & time" },
  { value: "current_year", label: "Current year", group: "Date & time" },
  { value: "tomorrow_date", label: "Tomorrow's date", group: "Date & time" },
];

/**
 * The catalog bucketed into optgroups, in STUDENT_VAR_GROUP_ORDER.
 * A ~50-entry flat <select> is unreadable; grouped it is scannable.
 */
export const STUDENT_VAR_GROUPS = STUDENT_VAR_GROUP_ORDER
  .map((group) => ({ group, vars: STUDENT_TEMPLATE_VARS.filter((v) => v.group === group) }))
  .filter((g) => g.vars.length);

/* ── Contact catalog (saved contact-group audiences) ────────────────────────
 *
 * Mirrors CONTACT_VARS in vc_be/utils/whatsappTemplateVars.js.
 *
 * ⚠️ **Four contact fields, and no student fields.** A contact list is imported
 * from outside the LMS, so `pending_fees` or `batch_name` would resolve for
 * whichever handful of numbers happen to match a student and come out blank for
 * everyone else — and the operator would not see it, because their own test
 * number is usually one of the ones that matches. The backend enforces the same
 * split (`varKeysFor`) and strips a student variable out of a contact send, so
 * offering one here would produce a picker whose choices silently do nothing.
 */
export const CONTACT_VAR_GROUP_ORDER = ["Contact", "Institute", "Date & time"];

export const CONTACT_TEMPLATE_VARS = [
  { value: "contact_name", label: "Contact name", group: "Contact" },
  { value: "contact_first_name", label: "Contact first name", group: "Contact" },
  { value: "contact_phone", label: "Contact phone", group: "Contact" },
  { value: "contact_course", label: "Contact course", group: "Contact" },

  ...STUDENT_TEMPLATE_VARS.filter((v) => v.group === "Institute" || v.group === "Date & time"),
];

export const CONTACT_VAR_GROUPS = CONTACT_VAR_GROUP_ORDER
  .map((group) => ({ group, vars: CONTACT_TEMPLATE_VARS.filter((v) => v.group === group) }))
  .filter((g) => g.vars.length);

/**
 * The picker's options for a given audience — the FE half of the backend's
 * `varKeysFor`. Audience types are the wire values used by the scheduled-message
 * and broadcast payloads.
 */
export const varGroupsForAudience = (audienceType) => (
  audienceType === "contact_groups" || audienceType === "external_numbers"
    ? CONTACT_VAR_GROUPS
    : STUDENT_VAR_GROUPS
);

const BY_VALUE = new Map(
  [...STUDENT_TEMPLATE_VARS, ...CONTACT_TEMPLATE_VARS].map((v) => [v.value, v]),
);

/** Human label for a stored variable key; falls back to the key itself so a
 *  mapping saved before a rename still reads as something. */
export const dynamicVarLabel = (value) => BY_VALUE.get(value)?.label || value;

export const isKnownStudentVar = (value) => BY_VALUE.has(value);

/** True when `value` resolves against the catalog `audienceType` uses. Lets the
 *  form flag a mapping that would be stripped server-side — e.g. a template set
 *  up for students whose audience is then switched to a contact group. */
export const isVarValidForAudience = (value, audienceType) =>
  varGroupsForAudience(audienceType).some((g) => g.vars.some((v) => v.value === value));
